import React from 'react';
import { Helmet } from 'react-helmet-async';

interface LinkedServiceType {
    title?: string | null;
}

interface ServiceLdJsonProps {
    title?: string | null;
    description?: string | null;
    url?: string | null;
    linkedServiceTypes?: ReadonlyArray<LinkedServiceType | null> | null;
}

/**
 * Generates Service JSON-LD schema for national service detail pages
 * This provides structured data for search engines about SERVPRO services
 */
const ServiceLdJson = ({
    title,
    description,
    url,
    linkedServiceTypes = [],
}: ServiceLdJsonProps): JSX.Element | null => {
    if (!title || !url) {
        return null;
    }

    const baseUrl = process.env.URL || 'https://www.servpro.com';
    const fullUrl = `${baseUrl}${url}`;

    // Build service categories from linked service types
    const serviceCategories = (linkedServiceTypes || [])
        .filter((st): st is LinkedServiceType => !!st?.title)
        .map(st => st.title)
        .filter((title): title is string => !!title);

    // Build Service schema
    const data = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: title,
        name: title,
        ...(description && { description }),
        url: fullUrl,
        image: {
            '@type': 'ImageObject',
            url: 'https://images.contentstack.io/v3/assets/blt0a0cb058815d4d96/blt9af1c0e2eaba012a/servpro_vehicle.jpeg?width=1200&auto=webp',
            width: 1200,
            height: 800,
        },
        provider: {
            '@type': 'Organization',
            name: 'SERVPRO',
            url: baseUrl,
            logo: 'https://images.contentstack.io/v3/assets/blt0a0cb058815d4d96/blt9af1c0e2eaba012a/servpro_vehicle.jpeg?width=1200&auto=webp',
            telephone: '1-800-SERVPRO',
            address: {
                '@type': 'PostalAddress',
                streetAddress: '801 Industrial Blvd',
                addressLocality: 'Gallatin',
                addressRegion: 'TN',
                postalCode: '37066',
                addressCountry: 'US',
                geo: {
                    '@type': 'GeoCoordinates',
                    latitude: 36.37294513955289,
                    longitude: -86.41210542806725,
                },
            },
        },
        areaServed: {
            '@type': 'Country',
            name: 'United States',
        },
        availableChannel: {
            '@type': 'ServiceChannel',
            serviceUrl: fullUrl,
            servicePhone: '1-800-SERVPRO',
            availableLanguage: {
                '@type': 'Language',
                name: 'English',
            },
        },
        // Add service categories if available
        ...(serviceCategories.length > 0 && {
            category: serviceCategories,
        }),
    };

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(data)}
            </script>
        </Helmet>
    );
};

export default ServiceLdJson;
